import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@shared/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@shared/ui/radio-group";
import { Label } from "@shared/ui/label";
import { Input } from "@shared/ui/input";
import { Button } from "@shared/ui/button";
import { useNavigate, useParams } from "react-router-dom";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useSchedulePlan } from "../../hooks";

interface EditPlanModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  currentVersion: string;
  planId?: string;
}

export const EditPlaneModal: React.FC<EditPlanModalProps> = ({
  isOpen,
  onOpenChange,
  currentVersion,
  planId,
}) => {
  console.log("currentVersion", currentVersion);
  const navigate = useNavigate();
  const { id: paramId } = useParams<{ id: string }>();
  const id = planId || paramId;
  const remoteUtilityId = useRemoteUtilityId();
  const schedulePlanMutation = useSchedulePlan();
  const [editOption, setEditOption] = useState<"new" | "schedule">("new");
  const [scheduleDate, setScheduleDate] = useState("");

  const parseVersionNumber = (ver: any): number => {
    if (ver === null || ver === undefined) return 1.0;
    if (typeof ver === "number") return isNaN(ver) ? 1.0 : ver;
    const str = String(ver);
    const match = str.match(/(\d+(?:\.\d+)?)/);
    if (match) {
      const num = parseFloat(match[1]);
      return isNaN(num) ? 1.0 : num;
    }
    return 1.0;
  };

  const currentVersionNumber = parseVersionNumber(currentVersion);
  const nextVersion = `v${(currentVersionNumber + 0.1).toFixed(1)}`;

  const handleContinue = () => {
    if (editOption === "schedule" && scheduleDate) {
      schedulePlanMutation.mutate(
        {
          planId: id || "",
          payload: { start_date: scheduleDate, schedule: true },
        },
        {
          onSuccess: () => {
            navigate(`/plan-tarrif/plan/edit/${id}`);
            onOpenChange(false);
          },
        },
      );
    } else {
      navigate(`/plan-tarrif/plan/edit/${id}`);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Editing an active rate</DialogTitle>
          <DialogDescription>
            Editing this tariff creates {nextVersion}. Historical billing
            remains unaffected.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <RadioGroup
            value={editOption}
            onValueChange={(val: any) => setEditOption(val)}
            className="gap-4"
          >
            {/* Option 1 */}
            <div
              className={`relative flex cursor-pointer rounded-lg border p-4 shadow-sm focus:outline-none ${
                editOption === "new"
                  ? "border-blue-600 ring-1 ring-blue-600 bg-blue-50/50"
                  : "border-gray-300"
              }`}
              onClick={() => setEditOption("new")}
            >
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center">
                  <div className="text-sm">
                    <RadioGroupItem
                      value="new"
                      id="new-version"
                      className="sr-only"
                    />
                    <Label
                      htmlFor="new-version"
                      className={`font-medium cursor-pointer ${
                        editOption === "new" ? "text-blue-900" : "text-gray-900"
                      }`}
                    >
                      Create New Version
                    </Label>
                    <p
                      className={`mt-1 cursor-pointer ${editOption === "new" ? "text-blue-700" : "text-gray-500"}`}
                    >
                      Roll all changes into {nextVersion} and apply on save.
                    </p>
                  </div>
                </div>
                <div
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                    editOption === "new" ? "border-blue-600" : "border-gray-300"
                  }`}
                >
                  {editOption === "new" && (
                    <span className="h-2.5 w-2.5 rounded-full bg-blue-600" />
                  )}
                </div>
              </div>
            </div>

            {/* Option 2 */}
            <div
              className={`relative flex cursor-pointer flex-col rounded-lg border p-4 shadow-sm focus:outline-none ${
                editOption === "schedule"
                  ? "border-blue-600 ring-1 ring-blue-600 bg-blue-50/50"
                  : "border-gray-300"
              }`}
              onClick={() => setEditOption("schedule")}
            >
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center">
                  <div className="text-sm">
                    <RadioGroupItem
                      value="schedule"
                      id="schedule-version"
                      className="sr-only"
                    />
                    <Label
                      htmlFor="schedule-version"
                      className={`font-medium cursor-pointer ${
                        editOption === "schedule"
                          ? "text-blue-900"
                          : "text-gray-900"
                      }`}
                    >
                      Schedule Future Version
                    </Label>
                    <p
                      className={`mt-1 cursor-pointer ${editOption === "schedule" ? "text-blue-700" : "text-gray-500"}`}
                    >
                      Apply changes on a future date — current version remains
                      active until then.
                    </p>
                  </div>
                </div>
                <div
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                    editOption === "schedule"
                      ? "border-blue-600"
                      : "border-gray-300"
                  }`}
                >
                  {editOption === "schedule" && (
                    <span className="h-2.5 w-2.5 rounded-full bg-blue-600" />
                  )}
                </div>
              </div>

              {/* Conditional Date Picker */}
              {editOption === "schedule" && (
                <div
                  className="mt-4 border-t border-blue-200 pt-4"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Label
                    htmlFor="activate-on"
                    className="text-sm font-medium text-blue-900 mb-1.5 block"
                  >
                    Activate on
                  </Label>
                  <Input
                    type="date"
                    id="activate-on"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    className="bg-white border-blue-200"
                  />
                </div>
              )}
            </div>
          </RadioGroup>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleContinue}
            disabled={
              (editOption === "schedule" && !scheduleDate) ||
              schedulePlanMutation.isPending
            }
          >
            {schedulePlanMutation.isPending ? "Scheduling..." : "Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
